import React, { useState, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    LinearProgress,
    Stepper,
    Step,
    StepLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Backdrop,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ListIcon from '@mui/icons-material/List';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { alpha } from '@mui/material/styles';

import { SCUIContent, SCUIS3Browser } from '../../../atomic/organisms';
import { SCUIButton } from '../../../atomic/atoms';
import { FileTransferServiceClient } from '../GRPCFileManager/proto/FileTransferServiceClient';
import { StudioFileUploadDropzone } from '../../components/StudioFileUploadDropzone';
import { getUsername } from '../../../core/wrappers/axios-wrapper';
import {
    RETOUCH_ACCEPT_MIME,
    validateRetouchFile,
    type RetouchFileValidationResult,
} from '../../utils/retouchFileValidation';
import {
    ensureRetouchS3Folder,
    parseRetouchBatchResult,
    resolveTransferStatus,
    shouldShowTransferButton,
} from './retouch.helpers';

type Mode = 'download' | 'upload' | null;

type FileListIngestState =
    | null
    | { mode: 'readingNative' }
    | {
          mode: 'ingesting';
          current: number;
          total: number;
          currentSize?: number;
          totalSize?: number;
          currentFileName?: string;
      };

function yieldToPaint(): Promise<void> {
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => resolve());
        });
    });
}

function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatTransferDuration(ms: number): string {
    if (!Number.isFinite(ms) || ms < 0) return '0:00';
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) {
        return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${m}:${String(s).padStart(2, '0')}`;
}

interface RetouchBatchUploadContext {
    priority: number;
    folderPrefix: string;
}

enum UploadStatus {
    Pending = 'Pending',
    Uploading = 'Uploading',
    Completed = 'Completed',
    Failed = 'Failed',
}

interface RetouchBatchSaveResult {
    successCount: number;
    errorCount: number;
    processedCount: number;
    errors: string[];
    requestId?: number;
}

interface UploadFileResult {
    success: boolean;
    batchResult?: RetouchBatchSaveResult;
    errorMessage?: string;
}

type StatusSeverity = 'success' | 'error' | 'warning' | 'info';

interface UploadItem {
    file: File;
    fileName: string;
    progress: number;
    status: UploadStatus;
    errorMessage: string;
    /** Dosya seçiminde doğrulama hatası (S3'e hiç gitmez). */
    isValidationError?: boolean;
}

const steps = ['studio.upload.selectFiles', 'studio.grpcFileManager.selection.title', 'general.summary'];

const FILE_LIST_PREVIEW_LIMIT = 100;

function getRetouchValidationErrorMessage(
    validation: RetouchFileValidationResult,
    t: (key: string, options?: Record<string, unknown>) => string
): string {
    switch (validation.errorCode) {
        case 'maxFileSize':
            return t('studio.retouch.validation.maxFileSize');
        case 'invalidAspectRatio':
            return t('studio.retouch.validation.invalidAspectRatio', {
                ratio: validation.ratio ?? '-',
                width: validation.width ?? '-',
                height: validation.height ?? '-',
            });
        case 'invalidFormat':
            return t('studio.retouch.validation.invalidFormat');
        case 'dimensionReadFailed':
            return t('studio.retouch.validation.dimensionReadFailed');
        case 'invalidFileName':
            return t('studio.retouch.validation.invalidFileName');
        case 'missingFrameInfo':
            return t('studio.retouch.validation.missingFrameInfo');
        default:
            return t('studio.retouch.validation.unknown');
    }
}

function buildRetouchObjectKey(folderPrefix: string, fileName: string): string {
    if (!folderPrefix) {
        return fileName;
    }
    const cleanPrefix = folderPrefix.endsWith('/') ? folderPrefix.slice(0, -1) : folderPrefix;
    return `${cleanPrefix}/${fileName}`;
}

export const Retouch: React.FC = () => {
    const { t } = useTranslation();
    const [mode, setMode] = useState<Mode>(null);
    const [s3Downloading, setS3Downloading] = useState(false);

    // Stepper State
    const [activeStep, setActiveStep] = useState(0);
    const [priority, setPriority] = useState<number>(2); // 1: Acil, 2: Standart
    const [s3DestinationPath, setS3DestinationPath] = useState<string>('');

    const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [transferElapsedMs, setTransferElapsedMs] = useState(0);
    const [lastTransferDurationMs, setLastTransferDurationMs] = useState<number | null>(null);
    const [fileListIngest, setFileListIngest] = useState<FileListIngestState>(null);
    const [earlyBottomAfterNativePicker, setEarlyBottomAfterNativePicker] = useState(false);
    const [fileListPreviewExpanded, setFileListPreviewExpanded] = useState(false);
    const waitingNativePickerReturnRef = useRef(false);
    const fileListIngestPhaseRef = useRef<FileListIngestState>(null);
    const fileIngestGenerationRef = useRef<number>(0);
    const uploadStartedAtRef = useRef<number | null>(null);
    const uploadTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [uploadStatusAccordionExpanded, setUploadStatusAccordionExpanded] = useState(true);
    const [ongoingSubExpanded, setOngoingSubExpanded] = useState(true);
    const [successSubExpanded, setSuccessSubExpanded] = useState(false);
    const [failedSubExpanded, setFailedSubExpanded] = useState(true);
    const [validFilesAccordionExpanded, setValidFilesAccordionExpanded] = useState(true);
    const [failedFilesAccordionExpanded, setFailedFilesAccordionExpanded] = useState(true);
    const [statusMessage, setStatusMessage] = useState<string>('');
    const [statusSeverity, setStatusSeverity] = useState<StatusSeverity>('info');
    const [statusDetails, setStatusDetails] = useState<string[]>([]);

    const isBuildingFileList = fileListIngest !== null;
    const isFileListPrepareUi = isBuildingFileList || earlyBottomAfterNativePicker;
    const transferUploadItems = useMemo(() => uploadItems.filter((item) => !item.isValidationError), [uploadItems]);
    const ongoingGroup = useMemo(
        () =>
            transferUploadItems.filter(
                (item) => item.status === UploadStatus.Uploading || item.status === UploadStatus.Pending
            ),
        [transferUploadItems]
    );
    const successGroup = useMemo(
        () => transferUploadItems.filter((item) => item.status === UploadStatus.Completed),
        [transferUploadItems]
    );
    const failedGroup = useMemo(
        () => transferUploadItems.filter((item) => item.status === UploadStatus.Failed),
        [transferUploadItems]
    );
    const validPendingFilesCount = useMemo(
        () => transferUploadItems.filter((item) => item.status === UploadStatus.Pending).length,
        [transferUploadItems]
    );
    const validationFailedItems = useMemo(() => uploadItems.filter((item) => item.isValidationError), [uploadItems]);
    const validationFailedFilesCount = validationFailedItems.length;
    const validSelectionItems = useMemo(() => uploadItems.filter((item) => !item.isValidationError), [uploadItems]);
    const isStep0Valid = validPendingFilesCount > 0 && !isFileListPrepareUi;

    const visibleValidSelectionItems = useMemo(() => {
        if (fileListPreviewExpanded || validSelectionItems.length <= FILE_LIST_PREVIEW_LIMIT) {
            return validSelectionItems;
        }
        return validSelectionItems.slice(0, FILE_LIST_PREVIEW_LIMIT);
    }, [validSelectionItems, fileListPreviewExpanded]);

    const visibleValidationFailedItems = useMemo(() => {
        if (fileListPreviewExpanded || validationFailedItems.length <= FILE_LIST_PREVIEW_LIMIT) {
            return validationFailedItems;
        }
        return validationFailedItems.slice(0, FILE_LIST_PREVIEW_LIMIT);
    }, [validationFailedItems, fileListPreviewExpanded]);

    const isValidListPreviewTruncated =
        validSelectionItems.length > FILE_LIST_PREVIEW_LIMIT && !fileListPreviewExpanded;
    const isFailedListPreviewTruncated =
        validationFailedItems.length > FILE_LIST_PREVIEW_LIMIT && !fileListPreviewExpanded;

    React.useEffect(() => {
        fileListIngestPhaseRef.current = fileListIngest;
    }, [fileListIngest]);

    React.useEffect(() => {
        if (activeStep !== 0) {
            waitingNativePickerReturnRef.current = false;
            setEarlyBottomAfterNativePicker(false);
        }
    }, [activeStep]);

    React.useEffect(() => {
        if (activeStep !== 0) {
            return;
        }

        let fallbackTimer: ReturnType<typeof globalThis.setTimeout> | undefined;

        const onWindowFocus = () => {
            if (!waitingNativePickerReturnRef.current) {
                return;
            }
            flushSync(() => setEarlyBottomAfterNativePicker(true));
            if (fallbackTimer !== undefined) {
                globalThis.clearTimeout(fallbackTimer);
            }
            fallbackTimer = globalThis.setTimeout(() => {
                if (!waitingNativePickerReturnRef.current) {
                    return;
                }
                const ingestActive = fileListIngestPhaseRef.current !== null;
                if (!ingestActive) {
                    waitingNativePickerReturnRef.current = false;
                    setEarlyBottomAfterNativePicker(false);
                }
            }, 900);
        };

        globalThis.addEventListener('focus', onWindowFocus);
        return () => {
            globalThis.removeEventListener('focus', onWindowFocus);
            if (fallbackTimer !== undefined) {
                globalThis.clearTimeout(fallbackTimer);
            }
        };
    }, [activeStep]);

    const UPLOAD_CHUNK_SIZE_BYTES = 10 * 1024 * 1024; // 10MB (S3 Multipart Upload requires parts >= 5MB)
    const grpcClient = useMemo(() => new FileTransferServiceClient(), []);
    const completedFilesCount = useMemo(
        () => transferUploadItems.filter((i) => i.status === UploadStatus.Completed).length,
        [transferUploadItems]
    );
    const showTransferButton = useMemo(
        () =>
            shouldShowTransferButton(
                transferUploadItems,
                isUploading,
                UploadStatus.Pending,
                UploadStatus.Completed,
                UploadStatus.Failed
            ),
        [transferUploadItems, isUploading]
    );

    const envPrefix = import.meta.env.VITE_ENVIRONMENT || 'DEV';
    const downloadBasePrefix = `${envPrefix}/igloo_share/`;

    const uploadBasePrefix = useMemo(() => {
        const envPref = String(import.meta.env.VITE_ENVIRONMENT ?? '')
            .trim()
            // sonar-disable-next-line S5852
            .replace(/\/+$/, '');
        const base = envPref ? `${envPref}/siteye_yuklenecekler/` : 'siteye_yuklenecekler/';

        const now = new Date();
        const year = now.getFullYear();
        const monthNum = String(now.getMonth() + 1).padStart(2, '0');

        const turkishMonths = [
            'OCAK',
            'ŞUBAT',
            'MART',
            'NİSAN',
            'MAYIS',
            'HAZİRAN',
            'TEMMUZ',
            'AĞUSTOS',
            'EYLÜL',
            'EKİM',
            'KASIM',
            'ARALIK',
        ];
        const monthName = turkishMonths[now.getMonth()];
        const monthFolder = `${monthNum}_${monthName}`;

        const day = String(now.getDate()).padStart(2, '0');
        const dateStr = `${day}.${monthNum}.${year}`;
        const yearFolder = `${year}ÇEKİMLER`;

        let username = 'SYSTEM';
        try {
            const checkOperator = JSON.parse(localStorage.getItem('checkOperator') || '{}');
            if (checkOperator?.username) {
                username = String(checkOperator.username).toLocaleUpperCase('tr-TR');
            }
        } catch (e) {
            console.error('Error getting username for S3 prefix', e);
        }

        return `${base}${yearFolder}/${monthFolder}/${dateStr}/${username}/`;
    }, []);

    const isS3DestinationValid = useMemo(() => {
        // sonar-disable-next-line S5852
        const base = uploadBasePrefix.replace(/\/+$/, '');
        // sonar-disable-next-line S5852
        const dest = (s3DestinationPath || '').replace(/\/+$/, '');
        if (!dest) return false;
        return dest === base || dest.startsWith(`${base}/`);
    }, [s3DestinationPath, uploadBasePrefix]);

    React.useEffect(() => {
        if (activeStep !== 1 || !uploadBasePrefix) {
            return;
        }

        ensureRetouchS3Folder(uploadBasePrefix);
    }, [activeStep, uploadBasePrefix]);

    const ingestFilesFromFileList = async (files: File[]) => {
        const gen = ++fileIngestGenerationRef.current;
        if (files.length === 0) {
            return;
        }

        const totalSize = files.reduce((acc, f) => acc + f.size, 0);
        let currentSizeProcessed = 0;

        flushSync(() => {
            setFileListIngest({
                mode: 'ingesting',
                current: 0,
                total: files.length,
                currentSize: 0,
                totalSize,
                currentFileName: '',
            });
            setUploadItems([]);
        });
        await yieldToPaint();

        const newItems: UploadItem[] = [];
        const n = files.length;

        for (let i = 0; i < n; i++) {
            if (fileIngestGenerationRef.current !== gen) {
                return;
            }
            const file = files[i];
            const validation = await validateRetouchFile(file);
            if (!validation.valid) {
                newItems.push({
                    file,
                    fileName: file.name,
                    progress: 0,
                    status: UploadStatus.Failed,
                    errorMessage: getRetouchValidationErrorMessage(validation, t),
                    isValidationError: true,
                });
            } else {
                newItems.push({
                    file,
                    fileName: file.name,
                    progress: 0,
                    status: UploadStatus.Pending,
                    errorMessage: '',
                    isValidationError: false,
                });
            }
            currentSizeProcessed += file.size;
            const done = i + 1;
            setFileListIngest({
                mode: 'ingesting',
                current: done,
                total: n,
                currentSize: currentSizeProcessed,
                totalSize,
                currentFileName: file.name,
            });
            await new Promise<void>((resolve) => {
                requestAnimationFrame(() => resolve());
            });
        }

        if (fileIngestGenerationRef.current !== gen) {
            return;
        }

        setUploadItems(newItems);
        setFileListPreviewExpanded(false);
        setFileListIngest(null);
    };

    const handleFilesSelected = async (selectedFiles: File[]) => {
        waitingNativePickerReturnRef.current = false;
        flushSync(() => {
            setEarlyBottomAfterNativePicker(false);
            setFileListIngest({ mode: 'readingNative' });
        });
        await yieldToPaint();

        if (selectedFiles.length === 0) {
            flushSync(() => setFileListIngest(null));
            return;
        }
        await ingestFilesFromFileList(selectedFiles);
    };

    const handleNativeFilePickerOpen = () => {
        waitingNativePickerReturnRef.current = true;
    };

    const handleNativeFilePickerCancel = () => {
        waitingNativePickerReturnRef.current = false;
        setEarlyBottomAfterNativePicker(false);
    };

    const removeFile = (fileName: string) => {
        setUploadItems((prev) => prev.filter((item) => item.fileName !== fileName));
    };

    const clearAllFiles = () => {
        fileIngestGenerationRef.current += 1;
        waitingNativePickerReturnRef.current = false;
        setEarlyBottomAfterNativePicker(false);
        setFileListIngest(null);
        setUploadItems([]);
        setFileListPreviewExpanded(false);
    };

    const uploadSingleFile = async (
        item: UploadItem,
        uploadContext: RetouchBatchUploadContext
    ): Promise<UploadFileResult> => {
        const preUploadValidation = await validateRetouchFile(item.file);
        if (!preUploadValidation.valid) {
            const errorMessage = getRetouchValidationErrorMessage(preUploadValidation, t);
            setUploadItems((prev) =>
                prev.map((ui) =>
                    ui.fileName === item.fileName
                        ? { ...ui, status: UploadStatus.Failed, errorMessage, progress: 0, isValidationError: true }
                        : ui
                )
            );
            return { success: false, errorMessage };
        }

        setUploadItems((prev) =>
            prev.map((ui) =>
                ui.fileName === item.fileName
                    ? { ...ui, status: UploadStatus.Uploading, errorMessage: '', progress: 0 }
                    : ui
            )
        );

        try {
            const file = item.file;
            let chunkIndex = 0;
            let totalBytesRead = 0;
            const totalChunks = Math.ceil(file.size / UPLOAD_CHUNK_SIZE_BYTES) || 1;
            const objectKey = buildRetouchObjectKey(uploadContext.folderPrefix, item.fileName);

            while (totalBytesRead < file.size || (file.size === 0 && chunkIndex === 0)) {
                const chunk = file.slice(totalBytesRead, totalBytesRead + UPLOAD_CHUNK_SIZE_BYTES);
                const arrayBuffer = await chunk.arrayBuffer();
                const uint8Array = new Uint8Array(arrayBuffer);

                const isLastChunk = chunkIndex === totalChunks - 1;

                await grpcClient.uploadRetouchChunk({
                    fileName: item.fileName,
                    chunkIndex,
                    data: uint8Array,
                    isLastChunk,
                    key: objectKey,
                    totalChunks,
                    priority: uploadContext.priority,
                    folderPrefix: uploadContext.folderPrefix,
                    isLastFileInBatch: false,
                    userName: getUsername() || undefined,
                });

                totalBytesRead += uint8Array.length;
                const progress = Math.min(100, Math.round((totalBytesRead / file.size) * 100));

                if (isLastChunk) {
                    setUploadItems((prev) =>
                        prev.map((ui) =>
                            ui.fileName === item.fileName
                                ? { ...ui, progress: 100, status: UploadStatus.Completed }
                                : ui
                        )
                    );
                    break;
                }

                setUploadItems((prev) => prev.map((ui) => (ui.fileName === item.fileName ? { ...ui, progress } : ui)));

                chunkIndex++;
            }

            return { success: true };
        } catch (error: any) {
            const errorMessage = error.message || t('studio.upload.uploadError');
            setUploadItems((prev) =>
                prev.map((ui) =>
                    ui.fileName === item.fileName ? { ...ui, status: UploadStatus.Failed, errorMessage } : ui
                )
            );
            return { success: false, errorMessage };
        }
    };

    const finalizeRetouchBatch = async (
        lastCompletedItem: UploadItem,
        completedBatchFileNames: string[],
        uploadContext: RetouchBatchUploadContext
    ): Promise<RetouchBatchSaveResult | undefined> => {
        if (completedBatchFileNames.length === 0) {
            return undefined;
        }

        const file = lastCompletedItem.file;
        const totalChunks = Math.ceil(file.size / UPLOAD_CHUNK_SIZE_BYTES) || 1;
        const objectKey = buildRetouchObjectKey(uploadContext.folderPrefix, lastCompletedItem.fileName);
        const lastChunkStart = Math.max(0, file.size - UPLOAD_CHUNK_SIZE_BYTES);
        const lastChunk = file.slice(lastChunkStart);
        const lastChunkData = new Uint8Array(await lastChunk.arrayBuffer());

        const chunkResult = await grpcClient.uploadRetouchChunk({
            fileName: lastCompletedItem.fileName,
            chunkIndex: totalChunks - 1,
            data: lastChunkData,
            isLastChunk: true,
            key: objectKey,
            totalChunks,
            priority: uploadContext.priority,
            folderPrefix: uploadContext.folderPrefix,
            isLastFileInBatch: true,
            batchFileNames: completedBatchFileNames,
            userName: getUsername() || undefined,
        });

        return parseRetouchBatchResult(chunkResult.message);
    };

    const applyTransferResult = (
        completedUploadCount: number,
        failedUploadCount: number,
        batchResult?: RetouchBatchSaveResult
    ) => {
        const status = resolveTransferStatus(completedUploadCount, failedUploadCount, batchResult, t);
        setStatusSeverity(status.severity);
        setStatusMessage(status.message);
        setStatusDetails(status.details);
    };

    const handleTransfer = async () => {
        if (uploadItems.length === 0) return;

        setIsUploading(true);
        setUploadStatusAccordionExpanded(true);
        setOngoingSubExpanded(true);
        setSuccessSubExpanded(false);
        setFailedSubExpanded(true);
        setStatusMessage(t('studio.upload.uploadStarted'));
        setStatusSeverity('info');
        setStatusDetails([]);

        const pendingItems = uploadItems.filter((item) => item.status === UploadStatus.Pending);
        const uploadContext: RetouchBatchUploadContext = {
            priority,
            folderPrefix: s3DestinationPath,
        };

        if (pendingItems.length === 0) {
            applyTransferResult(0, 0);
            setIsUploading(false);
            return;
        }

        if (uploadTimerRef.current != null) {
            clearInterval(uploadTimerRef.current);
            uploadTimerRef.current = null;
        }
        uploadStartedAtRef.current = performance.now();
        setTransferElapsedMs(0);
        setLastTransferDurationMs(null);
        uploadTimerRef.current = setInterval(() => {
            if (uploadStartedAtRef.current != null) {
                setTransferElapsedMs(Math.round(performance.now() - uploadStartedAtRef.current));
            }
        }, 250);

        try {
            const completedBatchFileNames: string[] = [];
            const completedItems: UploadItem[] = [];
            let failedUploadCount = 0;
            let batchResult: RetouchBatchSaveResult | undefined;

            for (const item of pendingItems) {
                const result = await uploadSingleFile(item, uploadContext);
                if (result.success) {
                    completedBatchFileNames.push(item.fileName);
                    completedItems.push(item);
                } else {
                    failedUploadCount += 1;
                }
            }

            if (completedBatchFileNames.length > 0) {
                const lastCompletedItem = completedItems[completedItems.length - 1];
                try {
                    batchResult = await finalizeRetouchBatch(lastCompletedItem, completedBatchFileNames, uploadContext);
                } catch (error: any) {
                    setStatusSeverity('error');
                    setStatusMessage(error?.message || t('studio.retouch.batchSaveFailed'));
                    setStatusDetails([
                        t('studio.retouch.uploadPartial', {
                            success: completedBatchFileNames.length,
                            failed: failedUploadCount,
                        }),
                    ]);
                    return;
                }
            }

            applyTransferResult(completedBatchFileNames.length, failedUploadCount, batchResult);
        } finally {
            if (uploadTimerRef.current != null) {
                clearInterval(uploadTimerRef.current);
                uploadTimerRef.current = null;
            }
            if (uploadStartedAtRef.current != null) {
                const duration = Math.round(performance.now() - uploadStartedAtRef.current);
                setLastTransferDurationMs(duration);
                uploadStartedAtRef.current = null;
            }
            setUploadStatusAccordionExpanded(true);
            setOngoingSubExpanded(false);
            setSuccessSubExpanded(false);
            setFailedSubExpanded(true);
            setIsUploading(false);
        }
    };

    const handleNext = () => {
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const resetForm = () => {
        fileIngestGenerationRef.current += 1;
        waitingNativePickerReturnRef.current = false;
        setEarlyBottomAfterNativePicker(false);
        setFileListIngest(null);
        setFileListPreviewExpanded(false);
        setActiveStep(0);
        setUploadItems([]);
        setIsUploading(false);
        setTransferElapsedMs(0);
        setLastTransferDurationMs(null);
        if (uploadTimerRef.current != null) {
            clearInterval(uploadTimerRef.current);
            uploadTimerRef.current = null;
        }
        uploadStartedAtRef.current = null;
        setUploadStatusAccordionExpanded(true);
        setOngoingSubExpanded(true);
        setSuccessSubExpanded(false);
        setFailedSubExpanded(true);
        setValidFilesAccordionExpanded(true);
        setFailedFilesAccordionExpanded(true);
        setPriority(2);
        setS3DestinationPath('');
        setStatusMessage('');
        setStatusSeverity('info');
        setStatusDetails([]);
    };

    const renderSelectionFileListItem = (item: UploadItem, index: number) => (
        <ListItem
            key={`${item.fileName}-${item.file.size}-${index}`}
            divider
            secondaryAction={
                <IconButton
                    edge="end"
                    size="small"
                    onClick={() => removeFile(item.fileName)}
                    disabled={isFileListPrepareUi}
                    sx={{ color: '#bdbdbd', '&:hover': { color: '#f44336' } }}
                >
                    <CancelIcon sx={{ fontSize: 18 }} />
                </IconButton>
            }
            sx={item.isValidationError ? { bgcolor: alpha('#f44336', 0.04) } : undefined}
        >
            <ListItemText
                primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {item.isValidationError && <ErrorOutlineIcon color="error" sx={{ fontSize: 18 }} />}
                        {!item.isValidationError && <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />}
                        <Typography
                            component="span"
                            variant="body2"
                            sx={{
                                wordBreak: 'break-all',
                                color: item.isValidationError ? 'error.main' : 'text.primary',
                            }}
                        >
                            {item.fileName}
                        </Typography>
                    </Box>
                }
                secondary={item.errorMessage ? item.errorMessage : `${Math.round(item.file.size / 1024)} KB`}
                slotProps={{
                    secondary: {
                        color: item.errorMessage ? 'error' : 'text.secondary',
                    },
                }}
            />
        </ListItem>
    );

    const renderUploadProgressItem = (item: UploadItem, index: number, total: number) => (
        <Box
            key={`${item.fileName}-${index}`}
            sx={{
                mb: index < total - 1 ? 2 : 0,
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                backgroundColor: 'white',
                boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
                <Typography variant="h6" sx={{ fontSize: '15px', flex: 1, wordBreak: 'break-all' }}>
                    {item.fileName}
                </Typography>
                {item.status === UploadStatus.Completed && <CheckCircleIcon color="success" fontSize="small" />}
                {item.status === UploadStatus.Failed && !item.isValidationError && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                )}
                <Typography variant="caption" color="text.secondary">
                    {item.progress}%
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={item.progress}
                color={item.status === UploadStatus.Failed ? 'error' : 'primary'}
            />
            {item.errorMessage && !item.isValidationError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    {item.errorMessage}
                </Typography>
            )}
        </Box>
    );

    if (mode === 'download') {
        return (
            <SCUIContent headerName={t('studio.retouch.download')}>
                <Box sx={{ mb: 2 }}>
                    <SCUIButton
                        type="secondary"
                        sizeType="default"
                        size="m"
                        text={t('general.back')}
                        onClick={() => setMode(null)}
                        disabled={s3Downloading}
                        showStartIcon
                        startIcon={<ArrowBackIcon />}
                    />
                </Box>
                <SCUIS3Browser
                    basePrefix={downloadBasePrefix}
                    disableUpload={true}
                    disableCreateFolder={true}
                    disableDelete={true}
                    disableRename={true}
                    disableMove={true}
                    showPreviewPanel={false}
                    hideContentWrapper={true}
                    onDownloadingChange={setS3Downloading}
                />
            </SCUIContent>
        );
    }

    if (mode === 'upload') {
        return (
            <SCUIContent headerName={t('studio.retouch.upload')}>
                <Backdrop
                    sx={{
                        color: '#fff',
                        zIndex: (theme) => theme.zIndex.drawer + 999,
                        backdropFilter: 'blur(4px)',
                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    }}
                    open={isFileListPrepareUi}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '90%',
                            maxWidth: 600,
                            textAlign: 'center',
                            p: 4,
                            borderRadius: 3,
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                            border: '1px solid rgba(255, 255, 255, 0.18)',
                        }}
                    >
                        <CircularProgress color="primary" size={50} sx={{ mb: 3 }} />
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            {fileListIngest?.mode === 'ingesting'
                                ? `Dosya hazırlanıyor (${fileListIngest.current} / ${fileListIngest.total})`
                                : 'Dosyalar taranıyor...'}
                        </Typography>
                        {fileListIngest?.mode === 'ingesting' && fileListIngest.currentFileName && (
                            <Typography
                                variant="body2"
                                sx={{
                                    mb: 3,
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    wordBreak: 'break-all',
                                    fontStyle: 'italic',
                                    px: 2,
                                }}
                            >
                                {fileListIngest.currentFileName}
                            </Typography>
                        )}
                        <Box sx={{ width: '100%', mb: 2 }}>
                            <LinearProgress
                                variant={fileListIngest?.mode === 'ingesting' ? 'determinate' : 'indeterminate'}
                                value={
                                    fileListIngest?.mode === 'ingesting' && fileListIngest.total > 0
                                        ? Math.min(100, (fileListIngest.current / fileListIngest.total) * 100)
                                        : undefined
                                }
                                sx={{
                                    height: 10,
                                    borderRadius: 5,
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 5,
                                    },
                                }}
                            />
                        </Box>
                        {fileListIngest?.mode === 'ingesting' &&
                            fileListIngest.totalSize !== undefined &&
                            fileListIngest.currentSize !== undefined && (
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                                    İlerleme:{' '}
                                    {Math.round((fileListIngest.currentSize / fileListIngest.totalSize) * 100)}% (
                                    {formatBytes(fileListIngest.currentSize)} / {formatBytes(fileListIngest.totalSize)})
                                </Typography>
                            )}
                    </Box>
                </Backdrop>

                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                    <SCUIButton
                        type="secondary"
                        sizeType="default"
                        size="m"
                        text={t('general.back')}
                        onClick={() => {
                            setMode(null);
                            resetForm();
                        }}
                        showStartIcon
                        startIcon={<ArrowBackIcon />}
                    />
                    <Box sx={{ width: '100%', mb: 4 }}>
                        <Stepper
                            activeStep={activeStep}
                            sx={{
                                '& .MuiStepLabel-label': { fontSize: '1.1rem' },
                                '& .MuiStepIcon-root': { fontSize: '2rem' },
                                '& .MuiStepIcon-text': { fill: '#fff' },
                            }}
                        >
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{t(label)}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                </Box>

                {activeStep === 0 && (
                    <Box
                        sx={{
                            mt: 4,
                            p: 3,
                            mb: 4,
                            borderRadius: 3,
                            border: '1px solid #e0e0e0',
                            backgroundColor: '#fff',
                            position: 'relative',
                            minHeight: { xs: 420, md: 520 },
                        }}
                    >
                        <StudioFileUploadDropzone
                            onFilesSelected={handleFilesSelected}
                            disabled={isBuildingFileList}
                            accept={RETOUCH_ACCEPT_MIME}
                            onBrowseOpen={handleNativeFilePickerOpen}
                            onBrowseCancel={handleNativeFilePickerCancel}
                        />

                        {validationFailedFilesCount > 0 && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {t('studio.retouch.validation.errorsTitle', { count: validationFailedFilesCount })}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t('studio.retouch.validation.errorsHint')}
                                </Typography>
                            </Alert>
                        )}

                        {uploadItems.length > 0 && (
                            <Box sx={{ mt: 2, mb: 3 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1,
                                    }}
                                >
                                    <Typography variant="subtitle2">
                                        {t('studio.upload.selectedFiles')} ({uploadItems.length})
                                    </Typography>
                                    <SCUIButton
                                        type="secondary"
                                        sizeType="default"
                                        size="s"
                                        text={t('studio.upload.clearAll')}
                                        onClick={clearAllFiles}
                                        disabled={isFileListPrepareUi}
                                        showStartIcon
                                        startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                                    />
                                </Box>
                                {isValidListPreviewTruncated && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mb: 1 }}
                                    >
                                        {t('studio.upload.fileListPreviewTruncated', {
                                            shown: FILE_LIST_PREVIEW_LIMIT,
                                            total: validSelectionItems.length,
                                        })}
                                    </Typography>
                                )}

                                {validationFailedItems.length > 0 && (
                                    <Accordion
                                        expanded={failedFilesAccordionExpanded}
                                        onChange={(_, expanded) => setFailedFilesAccordionExpanded(expanded)}
                                        disableGutters
                                        elevation={0}
                                        sx={{
                                            mb: 2,
                                            border: '1px solid #ffcdd2',
                                            borderRadius: '8px !important',
                                            overflow: 'hidden',
                                            '&:before': { display: 'none' },
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ color: 'error.dark' }} />}
                                            sx={{
                                                background: 'linear-gradient(180deg, #ffebee 0%, #ffcdd2 100%)',
                                                minHeight: 48,
                                                px: 2,
                                                '& .MuiAccordionSummary-content': {
                                                    my: 1,
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    width: '100%',
                                                    pr: 1,
                                                },
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ErrorOutlineIcon sx={{ color: 'error.dark', fontSize: 20 }} />
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 700, color: 'error.dark' }}
                                                >
                                                    {t('studio.upload.statusFailed')}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={validationFailedItems.length}
                                                size="small"
                                                sx={{
                                                    fontWeight: 700,
                                                    bgcolor: 'error.main',
                                                    color: 'error.contrastText',
                                                    height: 22,
                                                }}
                                            />
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            {isFailedListPreviewTruncated && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: 'block', px: 2, pt: 1 }}
                                                >
                                                    {t('studio.upload.fileListPreviewTruncated', {
                                                        shown: FILE_LIST_PREVIEW_LIMIT,
                                                        total: validationFailedItems.length,
                                                    })}
                                                </Typography>
                                            )}
                                            <List dense sx={{ maxHeight: 280, overflow: 'auto' }}>
                                                {visibleValidationFailedItems.map((item, index) =>
                                                    renderSelectionFileListItem(item, index)
                                                )}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {validSelectionItems.length > 0 && (
                                    <Accordion
                                        expanded={validFilesAccordionExpanded}
                                        onChange={(_, expanded) => setValidFilesAccordionExpanded(expanded)}
                                        disableGutters
                                        elevation={0}
                                        sx={{
                                            border: '1px solid #c8e6c9',
                                            borderRadius: '8px !important',
                                            overflow: 'hidden',
                                            '&:before': { display: 'none' },
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ color: 'success.dark' }} />}
                                            sx={{
                                                background: 'linear-gradient(180deg, #e8f5e9 0%, #c8e6c9 100%)',
                                                minHeight: 48,
                                                px: 2,
                                                '& .MuiAccordionSummary-content': {
                                                    my: 1,
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    width: '100%',
                                                    pr: 1,
                                                },
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CheckCircleIcon sx={{ color: 'success.dark', fontSize: 20 }} />
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 700, color: 'success.dark' }}
                                                >
                                                    {t('studio.upload.statusSuccess')}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={validSelectionItems.length}
                                                size="small"
                                                sx={{
                                                    fontWeight: 700,
                                                    bgcolor: 'success.main',
                                                    color: 'success.contrastText',
                                                    height: 22,
                                                }}
                                            />
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <List dense sx={{ maxHeight: 280, overflow: 'auto' }}>
                                                {visibleValidSelectionItems.map((item, index) =>
                                                    renderSelectionFileListItem(item, index)
                                                )}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {(isValidListPreviewTruncated || isFailedListPreviewTruncated) && (
                                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                                        <SCUIButton
                                            type="secondary"
                                            sizeType="default"
                                            size="s"
                                            text={
                                                fileListPreviewExpanded
                                                    ? t('studio.upload.showLessFiles')
                                                    : t('studio.upload.showAllFiles', { count: uploadItems.length })
                                            }
                                            onClick={() => setFileListPreviewExpanded((v) => !v)}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}

                        {isFileListPrepareUi && (
                            <Box sx={{ mt: 2, mb: 0.5 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 0.75 }}
                                >
                                    {fileListIngest?.mode === 'ingesting'
                                        ? t('studio.upload.fileListPreparingProgress', {
                                              current: fileListIngest.current,
                                              total: fileListIngest.total,
                                          })
                                        : t('studio.upload.fileListPrepareBottomHint')}
                                </Typography>
                                <LinearProgress
                                    variant={fileListIngest?.mode === 'ingesting' ? 'determinate' : 'indeterminate'}
                                    value={
                                        fileListIngest?.mode === 'ingesting' && fileListIngest.total > 0
                                            ? Math.min(100, (fileListIngest.current / fileListIngest.total) * 100)
                                            : undefined
                                    }
                                    sx={{ height: 6, borderRadius: 1 }}
                                />
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <SCUIButton
                                type="primary"
                                sizeType="default"
                                size="m"
                                text={t('general.next')}
                                onClick={handleNext}
                                disabled={!isStep0Valid}
                                showEndIcon
                                endIcon={<ArrowForwardIcon />}
                            />
                        </Box>
                    </Box>
                )}

                {activeStep === 1 && (
                    <Box sx={{ mt: 4 }}>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            {t(
                                'studio.retouch.step1Info',
                                'Lütfen işlem önceliğini ve dosyaların yükleneceği hedef klasörü seçiniz.'
                            )}
                        </Alert>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t('studio.grpcFileManager.selection.title')}
                        </Typography>
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel id="priority-select-label">
                                {t('studio.retouch.priority', 'Öncelik')}
                            </InputLabel>
                            <Select
                                labelId="priority-select-label"
                                value={priority}
                                label={t('studio.retouch.priority', 'Öncelik')}
                                onChange={(e) => setPriority(Number(e.target.value))}
                            >
                                <MenuItem value={1}>{t('studio.retouch.priorityUrgent', 'Acil')}</MenuItem>
                                <MenuItem value={2}>{t('studio.retouch.priorityStandard', 'Standart')}</MenuItem>
                            </Select>
                        </FormControl>

                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            {t('studio.upload.selectDestination', 'Hedef Klasör Seçimi')}
                        </Typography>
                        <Box
                            sx={{
                                height: 400,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                overflow: 'hidden',
                                mb: 3,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <SCUIS3Browser
                                basePrefix={uploadBasePrefix}
                                disableDownload={true}
                                disableUpload={true}
                                disableDelete={true}
                                disableRename={true}
                                disableMove={true}
                                showPreviewPanel={false}
                                hideContentWrapper={true}
                                onPrefixChange={(path) => setS3DestinationPath(path)}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <SCUIButton
                                type="secondary"
                                sizeType="default"
                                size="m"
                                text={t('general.back')}
                                onClick={handleBack}
                                showStartIcon
                                startIcon={<ArrowBackIcon />}
                            />
                            <SCUIButton
                                type="primary"
                                sizeType="default"
                                size="m"
                                text={t('general.next')}
                                onClick={handleNext}
                                disabled={!isS3DestinationValid}
                                showEndIcon
                                endIcon={<ArrowForwardIcon />}
                            />
                        </Box>
                    </Box>
                )}

                {activeStep === 2 && (
                    <Box sx={{ mt: 4 }}>
                        {!statusMessage && !isUploading && (
                            <Alert severity="info" sx={{ mb: 3 }}>
                                {t(
                                    'studio.retouch.step3Info',
                                    'Seçtiğiniz dosyalar aktarılmaya hazır. İşlemi başlatmak için "Aktar" butonuna tıklayınız.'
                                )}
                            </Alert>
                        )}
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t('general.summary')}
                        </Typography>

                        <Card variant="outlined" sx={{ mb: 3 }}>
                            <CardContent>
                                <Typography variant="body1">
                                    <strong>{t('studio.retouch.priority', 'Öncelik')}:</strong>{' '}
                                    {priority === 1
                                        ? t('studio.retouch.priorityUrgent', 'Acil')
                                        : t('studio.retouch.priorityStandard', 'Standart')}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>{t('studio.upload.destination', 'Hedef')}:</strong>{' '}
                                    {(() => {
                                        const path = s3DestinationPath?.trim();
                                        if (!path) return '—';
                                        const envPref = String(import.meta.env.VITE_ENVIRONMENT ?? '')
                                            .trim()
                                            // sonar-disable-next-line S5852
                                            .replace(/\/+$/, '');
                                        const baseToHide = envPref
                                            ? `${envPref}/siteye_yuklenecekler/`
                                            : 'siteye_yuklenecekler/';
                                        if (path.toLowerCase().startsWith(baseToHide.toLowerCase())) {
                                            return path.substring(baseToHide.length);
                                        }
                                        return path;
                                    })()}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>{t('studio.upload.fileCount', 'Dosya Sayısı')}:</strong>{' '}
                                    {validPendingFilesCount}
                                </Typography>
                                {validationFailedFilesCount > 0 && (
                                    <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                                        {t('studio.retouch.validation.excludedFromTransfer', {
                                            count: validationFailedFilesCount,
                                        })}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        {statusMessage && (
                            <Alert severity={statusSeverity} sx={{ mb: 3 }}>
                                <Typography variant="body2">{statusMessage}</Typography>
                                {statusDetails.length > 0 && (
                                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                                        {statusDetails.map((detail, detailIndex) => (
                                            <Typography
                                                key={`${detailIndex}-${detail}`}
                                                component="li"
                                                variant="caption"
                                            >
                                                {detail}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}
                            </Alert>
                        )}

                        {(isUploading || transferUploadItems.length > 0) && (
                            <Box sx={{ mb: 3 }}>
                                <Accordion
                                    expanded={uploadStatusAccordionExpanded}
                                    onChange={(_, expanded) => setUploadStatusAccordionExpanded(expanded)}
                                    disableGutters
                                    elevation={0}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: isUploading ? 'primary.light' : 'divider',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        backgroundColor: 'background.paper',
                                        height: '100%',
                                        boxShadow: isUploading
                                            ? '0 2px 12px rgba(25, 118, 210, 0.12)'
                                            : '0 1px 4px rgba(0,0,0,0.06)',
                                        '&:before': { display: 'none' },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={
                                            <ExpandMoreIcon
                                                sx={{
                                                    color: isUploading ? 'primary.main' : 'action.active',
                                                }}
                                            />
                                        }
                                        sx={{
                                            minHeight: 56,
                                            px: 2,
                                            py: 0.5,
                                            background: isUploading
                                                ? 'linear-gradient(105deg, #e3f2fd 0%, #f3e5f5 100%)'
                                                : 'linear-gradient(180deg, #fafafa 0%, #ffffff 100%)',
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            '& .MuiAccordionSummary-content': {
                                                my: 1,
                                                alignItems: 'center',
                                                gap: 1.25,
                                                flexWrap: 'wrap',
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                minWidth: 0,
                                                mr: 0.5,
                                            }}
                                        >
                                            <CloudUploadIcon
                                                sx={{
                                                    fontSize: 26,
                                                    color: isUploading ? 'primary.main' : 'text.secondary',
                                                    filter: isUploading
                                                        ? 'drop-shadow(0 1px 2px rgba(25,118,210,0.35))'
                                                        : 'none',
                                                }}
                                            />
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: isUploading ? 'primary.dark' : 'text.primary',
                                                    letterSpacing: 0.02,
                                                }}
                                            >
                                                {t('studio.upload.uploadStatus') || 'Upload Status'}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            icon={
                                                <ListIcon
                                                    sx={{
                                                        fontSize: 18,
                                                        color: isUploading ? 'primary.contrastText' : 'primary.main',
                                                    }}
                                                />
                                            }
                                            label={`${completedFilesCount}/${transferUploadItems.length}`}
                                            size="small"
                                            sx={(theme) => ({
                                                fontWeight: 700,
                                                fontVariantNumeric: 'tabular-nums',
                                                bgcolor: isUploading
                                                    ? theme.palette.primary.main
                                                    : alpha(theme.palette.primary.main, 0.12),
                                                color: isUploading
                                                    ? theme.palette.primary.contrastText
                                                    : theme.palette.primary.dark,
                                                border: '1px solid',
                                                borderColor: isUploading
                                                    ? theme.palette.primary.dark
                                                    : theme.palette.primary.light,
                                                '& .MuiChip-icon': { ml: '4px' },
                                            })}
                                        />
                                        {(isUploading || lastTransferDurationMs !== null) && (
                                            <Tooltip
                                                title={
                                                    isUploading
                                                        ? t('studio.upload.transferElapsed', {
                                                              time: formatTransferDuration(transferElapsedMs),
                                                          })
                                                        : t('studio.upload.transferTotalDuration', {
                                                              time: formatTransferDuration(lastTransferDurationMs!),
                                                          })
                                                }
                                                arrow
                                                placement="top"
                                            >
                                                <Chip
                                                    icon={
                                                        isUploading ? (
                                                            <HourglassEmptyOutlinedIcon
                                                                sx={{ fontSize: 18, color: 'warning.main' }}
                                                            />
                                                        ) : (
                                                            <AccessTimeIcon
                                                                sx={{ fontSize: 18, color: 'success.main' }}
                                                            />
                                                        )
                                                    }
                                                    label={formatTransferDuration(
                                                        isUploading ? transferElapsedMs : lastTransferDurationMs!
                                                    )}
                                                    size="small"
                                                    sx={(theme) => ({
                                                        fontWeight: 700,
                                                        fontVariantNumeric: 'tabular-nums',
                                                        bgcolor: isUploading
                                                            ? alpha(theme.palette.warning.main, 0.14)
                                                            : alpha(theme.palette.success.main, 0.14),
                                                        color: isUploading
                                                            ? theme.palette.warning.dark
                                                            : theme.palette.success.dark,
                                                        border: '1px solid',
                                                        borderColor: isUploading
                                                            ? theme.palette.warning.light
                                                            : theme.palette.success.light,
                                                        '& .MuiChip-icon': { ml: '4px' },
                                                    })}
                                                />
                                            </Tooltip>
                                        )}
                                    </AccordionSummary>
                                    <AccordionDetails
                                        sx={{
                                            px: 2,
                                            pb: 2,
                                            pt: 2,
                                        }}
                                    >
                                        <Accordion
                                            expanded={ongoingSubExpanded}
                                            onChange={(_, expanded) => setOngoingSubExpanded(expanded)}
                                            elevation={0}
                                            disableGutters
                                            sx={{
                                                border: '1px solid #ffe0b2',
                                                borderRadius: '8px !important',
                                                overflow: 'hidden',
                                                mb: 2,
                                                '&:before': { display: 'none' },
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon sx={{ color: 'warning.dark' }} />}
                                                sx={{
                                                    background: 'linear-gradient(180deg, #fff3e0 0%, #ffe0b2 100%)',
                                                    minHeight: 48,
                                                    height: 48,
                                                    px: 2,
                                                    '& .MuiAccordionSummary-content': {
                                                        my: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        width: '100%',
                                                        pr: 1,
                                                    },
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                    <HourglassEmptyOutlinedIcon
                                                        sx={{ color: 'warning.dark', fontSize: 20 }}
                                                    />
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{ fontWeight: 700, color: 'warning.dark' }}
                                                    >
                                                        {t('studio.upload.statusOngoing')}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={`${ongoingGroup.length}/${transferUploadItems.length}`}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 700,
                                                        bgcolor: 'warning.main',
                                                        color: 'warning.contrastText',
                                                        height: 20,
                                                        fontSize: '11px',
                                                    }}
                                                />
                                            </AccordionSummary>
                                            <AccordionDetails
                                                sx={{
                                                    p: 1.5,
                                                    maxHeight: 300,
                                                    overflowY: 'auto',
                                                    bgcolor: '#fcfcfc',
                                                }}
                                            >
                                                {ongoingGroup.length === 0 ? (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontStyle: 'italic',
                                                            textAlign: 'center',
                                                            py: 2,
                                                        }}
                                                    >
                                                        {t('general.noDataFound', 'Kayıt bulunamadı')}
                                                    </Typography>
                                                ) : (
                                                    ongoingGroup.map((item, index) =>
                                                        renderUploadProgressItem(item, index, ongoingGroup.length)
                                                    )
                                                )}
                                            </AccordionDetails>
                                        </Accordion>

                                        <Accordion
                                            expanded={successSubExpanded}
                                            onChange={(_, expanded) => setSuccessSubExpanded(expanded)}
                                            elevation={0}
                                            disableGutters
                                            sx={{
                                                border: '1px solid #c8e6c9',
                                                borderRadius: '8px !important',
                                                overflow: 'hidden',
                                                mb: 2,
                                                '&:before': { display: 'none' },
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon sx={{ color: 'success.dark' }} />}
                                                sx={{
                                                    background: 'linear-gradient(180deg, #e8f5e9 0%, #c8e6c9 100%)',
                                                    minHeight: 48,
                                                    height: 48,
                                                    px: 2,
                                                    '& .MuiAccordionSummary-content': {
                                                        my: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        width: '100%',
                                                        pr: 1,
                                                    },
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                    <CheckCircleIcon sx={{ color: 'success.dark', fontSize: 20 }} />
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{ fontWeight: 700, color: 'success.dark' }}
                                                    >
                                                        {t('studio.upload.statusSuccess')}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={`${successGroup.length}/${transferUploadItems.length}`}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 700,
                                                        bgcolor: 'success.main',
                                                        color: 'success.contrastText',
                                                        height: 20,
                                                        fontSize: '11px',
                                                    }}
                                                />
                                            </AccordionSummary>
                                            <AccordionDetails
                                                sx={{
                                                    p: 1.5,
                                                    maxHeight: 300,
                                                    overflowY: 'auto',
                                                    bgcolor: '#fcfcfc',
                                                }}
                                            >
                                                {successGroup.length === 0 ? (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontStyle: 'italic',
                                                            textAlign: 'center',
                                                            py: 2,
                                                        }}
                                                    >
                                                        {t('general.noDataFound', 'Kayıt bulunamadı')}
                                                    </Typography>
                                                ) : (
                                                    successGroup.map((item, index) =>
                                                        renderUploadProgressItem(item, index, successGroup.length)
                                                    )
                                                )}
                                            </AccordionDetails>
                                        </Accordion>

                                        <Accordion
                                            expanded={failedSubExpanded}
                                            onChange={(_, expanded) => setFailedSubExpanded(expanded)}
                                            elevation={0}
                                            disableGutters
                                            sx={{
                                                border: '1px solid #ffcdd2',
                                                borderRadius: '8px !important',
                                                overflow: 'hidden',
                                                '&:before': { display: 'none' },
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon sx={{ color: 'error.dark' }} />}
                                                sx={{
                                                    background: 'linear-gradient(180deg, #ffebee 0%, #ffcdd2 100%)',
                                                    minHeight: 48,
                                                    height: 48,
                                                    px: 2,
                                                    '& .MuiAccordionSummary-content': {
                                                        my: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        width: '100%',
                                                        pr: 1,
                                                    },
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                    <CancelIcon sx={{ color: 'error.dark', fontSize: 20 }} />
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{ fontWeight: 700, color: 'error.dark' }}
                                                    >
                                                        {t('studio.upload.statusFailed')}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={`${failedGroup.length}/${transferUploadItems.length}`}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 700,
                                                        bgcolor: 'error.main',
                                                        color: 'error.contrastText',
                                                        height: 20,
                                                        fontSize: '11px',
                                                    }}
                                                />
                                            </AccordionSummary>
                                            <AccordionDetails
                                                sx={{
                                                    p: 1.5,
                                                    maxHeight: 300,
                                                    overflowY: 'auto',
                                                    bgcolor: '#fcfcfc',
                                                }}
                                            >
                                                {failedGroup.length === 0 ? (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontStyle: 'italic',
                                                            textAlign: 'center',
                                                            py: 2,
                                                        }}
                                                    >
                                                        {t('general.noDataFound', 'Kayıt bulunamadı')}
                                                    </Typography>
                                                ) : (
                                                    failedGroup.map((item, index) =>
                                                        renderUploadProgressItem(item, index, failedGroup.length)
                                                    )
                                                )}
                                            </AccordionDetails>
                                        </Accordion>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <SCUIButton
                                type="secondary"
                                sizeType="default"
                                size="m"
                                text={t('general.back')}
                                onClick={handleBack}
                                disabled={isUploading}
                                showStartIcon
                                startIcon={<ArrowBackIcon />}
                            />
                            {showTransferButton ? (
                                <SCUIButton
                                    type="primary"
                                    sizeType="default"
                                    size="m"
                                    text={t('general.transfer', 'Aktar')}
                                    onClick={handleTransfer}
                                    disabled={isUploading || validPendingFilesCount === 0}
                                    showStartIcon
                                    startIcon={<CloudUploadIcon />}
                                />
                            ) : (
                                <SCUIButton
                                    type="primary"
                                    sizeType="default"
                                    size="m"
                                    text={t('general.newProcess')}
                                    onClick={resetForm}
                                    disabled={isUploading}
                                />
                            )}
                        </Box>
                    </Box>
                )}
            </SCUIContent>
        );
    }

    return (
        <SCUIContent headerName={t('studio.retouch.title')}>
            <Box
                sx={{
                    display: 'flex',
                    gap: 3,
                    flexWrap: 'wrap',
                    mt: 2,
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '50vh',
                }}
            >
                <Card sx={{ maxWidth: 345, flex: '1 1 300px' }}>
                    <CardActionArea onClick={() => setMode('download')} sx={{ height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 5 }}>
                            <CloudDownloadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                            <Typography gutterBottom variant="h5" component="div">
                                {t('studio.retouch.download')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('studio.retouch.downloadDesc')}
                            </Typography>
                        </CardContent>
                    </CardActionArea>
                </Card>

                <Card sx={{ maxWidth: 345, flex: '1 1 300px' }}>
                    <CardActionArea onClick={() => setMode('upload')} sx={{ height: '100%' }}>
                        <CardContent sx={{ textAlign: 'center', py: 5 }}>
                            <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                            <Typography gutterBottom variant="h5" component="div">
                                {t('studio.retouch.upload')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('studio.retouch.uploadDesc')}
                            </Typography>
                        </CardContent>
                    </CardActionArea>
                </Card>
            </Box>
        </SCUIContent>
    );
};
